import { useState } from "react";
import { Button, Col, message, Modal, Row, Spin, Table } from "antd";
import moment from "moment";
import { getExamInputs } from "../../../services/exam";
import { getAsset } from "../../../services/general";
import LinkedExamBanner from "./LinkedExamBanner";

const columnsWithUnit = [
  { title: 'Nombre', dataIndex: 'name', key: 'name', width: '50%' },
  { title: 'Resultado', dataIndex: 'result', key: 'result', width: '30%' },
  { title: 'Unidad', dataIndex: 'unit', key: 'unit', width: '20%' },
];

const columnsWithoutUnit = [
  { title: 'Nombre', dataIndex: 'name', key: 'name', width: '70%' },
  { title: 'Resultado', dataIndex: 'result', key: 'result', width: '30%' },
];


const hasValidResult = (item) => {
  if (item.input_type === 'file') return !!item.result_id;
  return item.result !== null && item.result !== undefined && String(item.result).trim() !== '';
};

const VigentExam = ({
  form,
  vigentExam,
  setFileList,
  handleConvertFile,
  onVigentExamResultsApplied,
  isInitialized = true,
  roles = [],
  toDoctor,
  disableForm,
  readOnly,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [vigentExamInputs, setVigentExamInputs] = useState(null);
  const [vigentExamApplied, setVigentExamApplied] = useState(false);

  const isFieldEditable = (field) => {
    if (field.blocked) return false;

    const isDoctor = roles.includes('doctor') || roles.includes('direccion_medica');
    const isPsychologist = roles.includes('equipo_psicologia');

    const isVisible = !field.medical_visibility || isDoctor || isPsychologist;
    const isEnabled = !readOnly && (
      (isDoctor && toDoctor)
      || (!disableForm && !isDoctor)
      || (isDoctor && field.medical_visibility)
      || (!disableForm && isPsychologist)
    );

    return isVisible && isEnabled;
  };

  const handleShowModal = async () => {
    setRows([]);
    setVigentExamInputs(null);
    setShowModal(true);
    setLoading(true);
    try {
      const response = await getExamInputs({ admissionPatientExamId: vigentExam.id });
      const visibleInputs = {
        ...response,
        procedures: (response.procedures || []).filter(isFieldEditable),
        measurements: (response.measurements || []).filter(isFieldEditable),
        questions: (response.questions || []).filter(isFieldEditable),
      };
      setVigentExamInputs(visibleInputs);

      const toRows = (items) =>
        (items || [])
          .filter(hasValidResult)
          .map(item => ({ name: item.name, result: item.input_type === 'file' ? (item.document_file_name || 'Archivo adjunto') : item.result, unit: item.unit || '' }));

      setRows([
        ...toRows(visibleInputs.procedures),
        ...toRows(visibleInputs.measurements),
        ...toRows(visibleInputs.questions),
      ]);
    } catch (error) {
      console.log('Error', error);
    } finally {
      setLoading(false);
    }
  };

  const hasResults = rows.length > 0;
  const hasUnits = rows.some(row => !!row.unit);

  const handleApplyResults = async () => {
    if (!vigentExamInputs) return;
    setLoading(true);
    try {
      const newValues = {};
      const filePromises = [];
      ['procedures', 'measurements', 'questions'].forEach(type => {
        if (vigentExamInputs[type]) {
          newValues[type] = {};
          vigentExamInputs[type].forEach(item => {
            if (item.input_type === 'date') {
              const parsed = item.result ? moment(item.result, 'DD-MM-YYYY') : null;
              newValues[type][item.id] = parsed?.isValid() ? parsed : null;
            } else if (item.input_type === 'time') {
              const parsed = item.result ? moment(item.result, 'HH:mm') : null;
              newValues[type][item.id] = parsed?.isValid() ? parsed : null;
            } else if (item.input_type === 'select_multiple' && item.result) {
              newValues[type][item.id] = Array.isArray(item.result)
                ? item.result
                : item.result.split(',').map(value => value.trim());
            } else {
              newValues[type][item.id] = item.result;
            }
            if (type === 'procedures' && item.input_type === 'file' && item.result_id) {
              filePromises.push(
                getAsset({ field: 'document', id: item.result_id, type: 'AdmissionProcedureResult' })
                  .then(({ document_url }) => fetch(document_url))
                  .then(response => response.blob())
                  .then(blob => {
                    const fetchedFile = new File([blob], item.document_file_name || 'archivo', { type: item.document_content_type || blob.type });
                    setFileList(prev => ({ ...prev, [item.id]: [fetchedFile] }));
                    handleConvertFile(fetchedFile, item);
                    return item.id;
                  })
              );
            }
          });
        }
      });
      const fileResults = await Promise.allSettled(filePromises);
      if (fileResults.some(result => result.status === 'rejected')) {
        message.error({ content: 'Error al cargar algunos archivos del examen vigente', style: { marginTop: '45vh' } });
      }
      const succeededFileIds = new Set(fileResults.filter(result => result.status === 'fulfilled').map(result => result.value));
      form.setFieldsValue(newValues);
      setShowModal(false);
      setRows([]);
      setVigentExamInputs(null);
      const vigentExamResultIds = [];
      ['procedures', 'measurements', 'questions'].forEach(type => {
        if (vigentExamInputs[type]) vigentExamInputs[type]
          .filter(item => item.input_type === 'file' ? succeededFileIds.has(item.id) : hasValidResult(item))
          .forEach(item => vigentExamResultIds.push(item.id));
      });
      onVigentExamResultsApplied?.(vigentExamResultIds);
      setVigentExamApplied(true);
    } catch (error) {
      console.error('Error applying vigent exam results', error);
      message.error({ content: 'Error al aplicar los resultados del examen vigente', style: { marginTop: '45vh' } });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setRows([]);
    setVigentExamInputs(null);
  };

  const handleReset = () => {
    form.resetFields();
    setFileList({});
    onVigentExamResultsApplied?.([]);
    setVigentExamApplied(false);
  };

  const modalFooter = (
    <Row justify="end" gutter={8}>
      <Col>
        <Button className="cmt-button primary outline" onClick={handleCancel}>Cancelar</Button>
      </Col>
      <Col>
        <Button className="cmt-button primary" type="primary" onClick={handleApplyResults} disabled={loading || !hasResults || !isInitialized}>Usar resultados</Button>
      </Col>
    </Row>
  );

  return (
    <>
      <Modal
        title="Examen vigente"
        open={showModal}
        onCancel={handleCancel}
        footer={modalFooter}
        wrapClassName="vigent-exam-modal"
        width={700}
      >
        {loading ? (
          <div className="text-center p-40">
            <Spin size="large" />
          </div>
        ) : hasResults ? (
          <Table
            dataSource={rows}
            columns={hasUnits ? columnsWithUnit : columnsWithoutUnit}
            rowKey={(_, i) => i}
            pagination={false}
            size="small"
          />
        ) : (
          <div className="text-center">Sin resultados para mostrar</div>
        )}
      </Modal>

      <div className="vigent-exam mt-20 mb-30">
        <LinkedExamBanner variant="vigent" examInfo={vigentExam} className="mb-20" />
        <Row justify="start" gutter={15}>
          <Col>
            <Button
              className="cmt-button secondary outline vigent-exam-button"
              type="primary"
              size="small"
              onClick={handleShowModal}
              loading={loading}
            >
              Ver examen vigente
            </Button>
          </Col>
          <Col>
            <Button
              className="cmt-button primary outline vigent-exam-button"
              type="primary"
              size="small"
              onClick={handleReset}
              disabled={!isInitialized || !vigentExamApplied}
            >
              Restablecer formulario
            </Button>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default VigentExam;
